import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { AdminHeader, Card, Empty } from "@/components/admin/ui";
import { Truck, CheckCircle2, AlertCircle } from "lucide-react";

export default function AdminShipping() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/admin/shipping").then(({ data }) => setData(data)); }, []);
  if (!data) return null;

  return (
    <div>
      <AdminHeader title="Shipping" subtitle="Courier shipments & provider status" />
      <Card className="p-5 mb-6">
        <div className="flex items-center gap-3">
          <span className="h-11 w-11 rounded-lg bg-slate-100 grid place-items-center"><Truck className="h-6 w-6 text-slate-600" /></span>
          <div className="flex-1">
            <p className="font-semibold text-slate-900 capitalize">{data.provider.provider}</p>
            <p className="text-sm text-slate-500">Primary shipping provider</p>
          </div>
          {data.provider.configured
            ? <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600"><CheckCircle2 className="h-4 w-4" /> Connected</span>
            : <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-600"><AlertCircle className="h-4 w-4" /> Not Configured</span>}
        </div>
        {!data.provider.configured && <p className="text-xs text-slate-500 mt-3 p-3 bg-amber-50 border border-amber-100 rounded-lg">Add <code className="font-mono">DELHIVERY_API_TOKEN</code> to the backend environment to enable live serviceability, rates, AWB generation and tracking. Credentials are stored server-side only.</p>}
      </Card>

      <Card className="p-5 mb-6">
        <h3 className="font-semibold text-slate-900 mb-1">Rate calculation</h3>
        <p className="text-xs text-slate-500 mb-4">
          Checkout charges the live courier rate for the delivery pincode (with the free-shipping threshold honoured first).
          These assumptions are used when asking Delhivery for a rate — nothing is invented per product.
        </p>
        <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <Row label="Delhivery environment" value={data.provider.environment} />
          <Row label="Pickup pincode (origin)" value={data.provider.originPincode || "Not configured"} />
          <Row label="Pickup location name" value={data.provider.pickupName || "Not configured"} />
          <Row label="Live rates at checkout" value={data.provider.liveRates ? "Enabled" : "Disabled (flat rate / free threshold)"} />
          <Row label="Legacy stored weight unit" value={data.provider.weightUnit} />
          <Row label="Weight for items without one" value={`${data.provider.defaultWeightGrams} g`} />
        </dl>
        <p className="text-xs text-slate-500 mt-3" data-testid="shipping-estimate-note">
          Delhivery shipping charges returned by this API are <strong>estimates</strong> (gross amount plus taxes) and may
          differ from the final courier invoice. Product weights entered in a product form are stored in grams with an
          explicit unit; the unit above applies only to older weights saved without one.
        </p>
        {!data.provider.liveRates && data.provider.configured && (
          <p className="text-xs text-amber-600 mt-3 p-3 bg-amber-50 border border-amber-100 rounded-lg">
            Live courier rates are switched off (<code className="font-mono">DELHIVERY_LIVE_RATES=false</code>), so checkout uses the
            configured flat rate / free-shipping threshold.
          </p>
        )}
      </Card>

      <Card className="p-5 mb-6">
        <h3 className="font-semibold text-slate-900 mb-1">Variant weights</h3>
        <p className="text-xs text-slate-500 mb-4">
          Delhivery is asked for a rate using the weight of the variant the customer selected. Variants without a weight
          use the fallback above.
        </p>
        {data.weights ? (
          <>
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="text-slate-700"><strong className="font-mono">{data.weights.withWeight}</strong> variant(s) with a weight</span>
              <span className={data.weights.withoutWeight ? "text-amber-600" : "text-slate-700"} data-testid="weights-without-weight">
                <strong className="font-mono">{data.weights.withoutWeight}</strong> using the fallback estimate
              </span>
              <span className="text-slate-500">of {data.weights.totalVariants} total</span>
            </div>
            {data.weights.withoutWeight > 0 && (
              <>
                <p className="text-xs text-amber-600 mt-3" data-testid="weights-fallback-note">
                  These variants have no weight, so their parcels are estimated at {data.provider.defaultWeightGrams} g each.
                  Add a weight (grams) in the product form to make shipping charges exact.
                </p>
                {data.weights.examples?.length > 0 && (
                  <ul className="text-xs text-slate-600 mt-2 list-disc list-inside font-mono">
                    {data.weights.examples.map((e, i) => <li key={i}>{e.name} — {e.sku || "no SKU"}</li>)}
                  </ul>
                )}
              </>
            )}
          </>
        ) : (
          <p className="text-xs text-slate-500">Weight coverage could not be read right now.</p>
        )}
      </Card>

      <h3 className="font-semibold text-slate-900 mb-3">Shipments</h3>
      <Card className="overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="p-3">Order</th><th className="p-3">Fulfillment</th><th className="p-3">AWB</th><th className="p-3">Shipment</th><th className="p-3">Pickup</th><th className="p-3">Pincode</th><th className="p-3">Date</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {data.shipments.map((s) => (
              <tr key={s.orderNumber} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/admin/orders/${s.orderId}`)}>
                <td className="p-3 font-mono">{s.orderNumber}</td>
                <td className="p-3">{s.fulfillmentStatus ? String(s.fulfillmentStatus).replace(/_/g, " ") : "—"}</td>
                <td className="p-3 font-mono text-xs">{s.awb || "—"}</td>
                <td className="p-3 text-xs">{s.shipmentStatus || "—"}</td>
                <td className="p-3 text-xs">{s.pickup?.status ? `${s.pickup.status}${s.pickup.date ? " · " + s.pickup.date : ""}` : "—"}</td>
                <td className="p-3 font-mono">{s.address?.pincode || "—"}</td>
                <td className="p-3 text-xs text-slate-500">{new Date(s.createdAt).toLocaleDateString("en-IN")}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.shipments.length === 0 && <Empty text="No shipments yet" />}
      </Card>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between gap-4">
    <dt className="text-slate-500">{label}</dt>
    <dd className="font-mono text-slate-900 text-right">{value}</dd>
  </div>
);
